import { useState } from 'react'
import { ApiError } from '@/api/errors'
import type { QCRemark } from '@/api/types'
import { qcSeverityLabel, qcStatusLabel } from '@/i18n/labels'
import { Button, Field, Sheet, Textarea } from '@/ui'
import {
  useQCReasonTransition,
  useQCSimpleTransition,
  type QCReasonTransition,
  type QCSimpleTransition,
} from './queries'

const SIMPLE_BY_STATUS: Record<QCRemark['status'], { t: QCSimpleTransition; label: string }[]> = {
  OPEN: [{ t: 'acknowledge', label: 'Принять' }],
  ACKNOWLEDGED: [{ t: 'resolve', label: 'Устранено' }],
  RESOLVED: [{ t: 'verify', label: 'Подтвердить' }],
  VERIFIED: [],
  REJECTED: [],
}

const REASON_BY_STATUS: Record<QCRemark['status'], { t: QCReasonTransition; label: string }[]> = {
  OPEN: [{ t: 'reject', label: 'Отклонить' }],
  ACKNOWLEDGED: [{ t: 'reject', label: 'Отклонить' }],
  RESOLVED: [{ t: 'reopen', label: 'Вернуть' }],
  VERIFIED: [{ t: 'reopen', label: 'Переоткрыть' }],
  REJECTED: [],
}

export function QCRemarkSheet({
  orderId,
  remark,
  open,
  onClose,
}: {
  orderId: string
  remark: QCRemark
  open: boolean
  onClose: () => void
}) {
  const simple = useQCSimpleTransition(orderId)
  const reason = useQCReasonTransition(orderId)
  const [reasonText, setReasonText] = useState('')

  const simpleActions = SIMPLE_BY_STATUS[remark.status]
  const reasonActions = REASON_BY_STATUS[remark.status]
  const err = simple.error ?? reason.error
  const errMessage = err instanceof ApiError ? err.message : null
  const busy = simple.isPending || reason.isPending

  return (
    <Sheet open={open} onClose={onClose} title={remark.title}>
      <p className="mb-3 text-sm text-tg-hint">
        {qcSeverityLabel[remark.severity]} · {qcStatusLabel[remark.status]}
      </p>
      {remark.description && <p className="mb-3 text-sm text-tg-text">{remark.description}</p>}

      {reasonActions.length > 0 && (
        <Field label="Причина (для возврата/отклонения)">
          <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} />
        </Field>
      )}

      <div className="flex flex-col gap-2">
        {simpleActions.map((a) => (
          <Button
            key={a.t}
            fullWidth
            loading={busy}
            onClick={() =>
              simple.mutate(
                { id: remark.id, transition: a.t, version: remark.version },
                { onSuccess: onClose },
              )
            }
          >
            {a.label}
          </Button>
        ))}
        {reasonActions.map((a) => (
          <Button
            key={a.t}
            fullWidth
            variant="secondary"
            loading={busy}
            disabled={reasonText.trim().length === 0}
            onClick={() =>
              reason.mutate(
                { id: remark.id, transition: a.t, version: remark.version, reason: reasonText.trim() },
                { onSuccess: onClose },
              )
            }
          >
            {a.label}
          </Button>
        ))}
        {simpleActions.length === 0 && reasonActions.length === 0 && (
          <p className="text-sm text-tg-hint">Замечание закрыто — действий нет.</p>
        )}
      </div>

      {errMessage && (
        <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-risk-red">{errMessage}</p>
      )}
    </Sheet>
  )
}
